import { lazy, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "../components/Loadable";
import RouteGuard from "../components/RouteGuard/RouteGuard";
import {
  ADMIN,
  CUSTOMER,
  DRIVER,
  STAFF,
  SUPER_ADMIN,
} from "../config/constants";
import HODashboardLayout from "../layouts/dashboardsLayouts/HODashboardLayout";
import Profile from "../pages/Profile/Profile";
import {
  IAuthState,
  resetLoggedOut,
  tryConfirmLogin,
} from "../stateManagement/reducers/authReducer";
import { IRootReducerT } from "../stateManagement/reduxStore";

interface IPageTemplateProps {
  altRoute: string;
  page: ReactNode;
  pageName: string;
}

const PageTemplate: React.FC<IPageTemplateProps> = ({
  pageName,
  altRoute,
  page,
}) => {
  const dispatch = useDispatch<any>();
  const { authToken, user, loggedOut } = useSelector<IRootReducerT, IAuthState>(
    (state) => state.auth,
  );
  let conditions: boolean[] = [];
  const hasValidAuthToken = authToken !== null;
  const isTime4Email2FA =
    hasValidAuthToken &&
    user?.usesEmail2FA === true &&
    user.email2FADone === false;
  const isTime4Authenticator2FA =
    hasValidAuthToken &&
    !isTime4Email2FA &&
    user?.usesAuthenticator2FA === true &&
    user.authenticator2FADone === false;
  const isCustomer = user && user?.role?.name === CUSTOMER ? true : false;
  const isAdmin =
    user?.role?.name === ADMIN || user?.role?.name === SUPER_ADMIN;
  const isStaff = user?.role?.name === STAFF;
  const isDriver = user?.role?.name === DRIVER;

  const isAuthenticated =
    hasValidAuthToken && !isTime4Email2FA && !isTime4Authenticator2FA;

  switch (pageName) {
    case "dashboard":
      conditions = [isAuthenticated, !isCustomer];
      break;
    case "order-history":
      conditions = [isAdmin || isDriver || isStaff];
      break;
    case "payment-history":
      conditions = [isAdmin || isCustomer];
      break;
    case "stats":
      conditions = [isAdmin];
      break;
    case "unapproved-orders":
      conditions = [isStaff || isCustomer];
      break;
    case "active-orders":
      conditions = [isStaff || isCustomer];
      break;
    case "completed-orders":
      conditions = [isStaff || isCustomer];
      break;
    case "present-orders-for-delivery":
      conditions = [isDriver];
      break;
    case "present-orders-for-pick-up":
    case "assigned-active-internal-services":
      conditions = [isDriver];
      break;
    default:
      conditions = [isAuthenticated, !isCustomer]; //User must be authenticated to access this route
      break;
  }

  const [isInitRender120, setIsInitRender120] = useState(true);
  useEffect(() => {
    if (isInitRender120) {
      setIsInitRender120(false);
      dispatch(resetLoggedOut({ authToken: authToken ?? "" }));
      return;
    }

    if (loggedOut) {
      window.open("/", "_self");
    }
  }, [loggedOut]);

  useEffect(() => {
    if (!user && authToken) {
      dispatch(tryConfirmLogin({ authToken }));
    }
  }, [authToken]);

  return (
    <>
      <RouteGuard conditions={conditions} altRoute={altRoute} />
      {page}
    </>
  );
};

//lazy pages
const LHODashboard = Loadable(
  lazy(() => import("../pages/dashboards/HODashboard")),
);
const LAdmins = Loadable(lazy(() => import("../pages/Admins/Admins")));
const LTutors = Loadable(lazy(() => import("../pages/Staff/Staff")));
const LCustomers = Loadable(lazy(() => import("../pages/Customers/Customers")));
const LCourses = Loadable(lazy(() => import("../pages/Courses/Courses")));
const LArticles = Loadable(lazy(() => import("../pages/Articles/Articles")));
const LSellableProducts = Loadable(
  lazy(() => import("../pages/SellableProducts/SellableProducts")),
);
const LSellableServices = Loadable(
  lazy(() => import("../pages/SellableServices/SellableServices")),
);
const LRecorededWorkshops = Loadable(
  lazy(() => import("../pages/RecordedWorkshops/RecordedWorkshops")),
);
const LEvents = Loadable(lazy(() => import("../pages/Events/Events")));
const LOrders = Loadable(lazy(() => import("../pages/Orders/Orders")));
const LCompletedOrders = Loadable(
  lazy(() => import("../pages/Orders/CompletedOrders")),
);
const LActiveOrders = Loadable(
  lazy(() => import("../pages/Orders/ActiveOrders")),
);
const LUnApprovedOrders = Loadable(
  lazy(() => import("../pages/Orders/UnApprovedOrders")),
);
const LPresentOrders4Delivery = Loadable(
  lazy(() => import("../pages/Orders/PresentOrders4Delivery")),
);
const LPresentOrders4PickUp = Loadable(
  lazy(() => import("../pages/Orders/PresentOrders4PickUp")),
);
const LSites = Loadable(lazy(() => import("../pages/Sites/Sites")));

const LProfile = Loadable(lazy(() => import("../pages/Profile/Profile")));

const DashboardRoutes = {
  path: "/",
  element: (
    <PageTemplate
      pageName=""
      altRoute={"/welcome"}
      page={<HODashboardLayout />}
    />
  ),
  children: [
    {
      path: "dashboard",
      element: (
        <PageTemplate
          pageName="dashboard"
          altRoute={"/welcome"}
          page={<LHODashboard />}
        />
      ),
    },
    {
      path: "dashboard/admins",
      element: (
        <PageTemplate
          pageName="dashboard/admins"
          altRoute={"/welcome"}
          page={<LAdmins />}
        />
      ),
    },
    {
      path: "dashboard/staff",
      element: (
        <PageTemplate
          pageName="dashboard/staff"
          altRoute={"/welcome"}
          page={<LTutors />}
        />
      ),
    },
    {
      path: "dashboard/sites",
      element: (
        <PageTemplate
          pageName="dashboard/sites"
          altRoute={"/welcome"}
          page={<LSites />}
        />
      ),
    },
    {
      path: "dashboard/members",
      element: (
        <PageTemplate
          pageName="dashboard/members"
          altRoute={"/welcome"}
          page={<LCustomers />}
        />
      ),
    },
    {
      path: "dashboard/courses",
      element: (
        <PageTemplate
          pageName="dashboard/courses"
          altRoute={"/welcome"}
          page={<LCourses />}
        />
      ),
    },
    {
      path: "dashboard/articles",
      element: (
        <PageTemplate
          pageName="dashboard/articles"
          altRoute={"/welcome"}
          page={<LArticles />}
        />
      ),
    },
    {
      path: "dashboard/products",
      element: (
        <PageTemplate
          pageName="dashboard/products"
          altRoute={"/welcome"}
          page={<LSellableProducts />}
        />
      ),
    },
    {
      path: "dashboard/services",
      element: (
        <PageTemplate
          pageName="dashboard/services"
          altRoute={"/welcome"}
          page={<LSellableServices />}
        />
      ),
    },
    {
      path: "dashboard/recorded-workshops",
      element: (
        <PageTemplate
          pageName="dashboard/recorded-workshops"
          altRoute={"/welcome"}
          page={<LRecorededWorkshops />}
        />
      ),
    },
    {
      path: "dashboard/events",
      element: (
        <PageTemplate
          pageName="dashboard/events"
          altRoute={"/welcome"}
          page={<LEvents />}
        />
      ),
    },
    {
      path: "unapproved-orders",
      element: (
        <PageTemplate
          pageName="unapproved-orders"
          altRoute={"/dashboard"}
          page={<LUnApprovedOrders />}
        />
      ),
    },
    {
      path: "active-orders",
      element: (
        <PageTemplate
          pageName="active-orders"
          altRoute={"/dashboard"}
          page={<LActiveOrders />}
        />
      ),
    },
    {
      path: "completed-orders",
      element: (
        <PageTemplate
          pageName="completed-orders"
          altRoute={"/dashboard"}
          page={<LCompletedOrders />}
        />
      ),
    },
    {
      path: "present-orders-for-delivery",
      element: (
        <PageTemplate
          pageName="present-orders-for-delivery"
          altRoute={"/dashboard"}
          page={<LPresentOrders4Delivery />}
        />
      ),
    },
    {
      path: "present-orders-for-pick-up",
      element: (
        <PageTemplate
          pageName="present-orders-for-pick-up"
          altRoute={"/dashboard"}
          page={<LPresentOrders4PickUp />}
        />
      ),
    },
    {
      path: "dashboard/order-history",
      element: (
        <PageTemplate
          pageName="order-history"
          altRoute={"/dashboard"}
          page={<LOrders />}
        />
      ),
    },
    {
      path: "profile",
      element: <Profile />,
    },
  ],
};

export default DashboardRoutes;
